import { ClientConfig, Config, Text, TextId } from "@resamare/models";
import { Spinner } from "@resamare/ui";
import { createContext, PropsWithChildren, useContext, useMemo } from "react";
import { useGetConfig } from "../../config";
import { useGetTexts } from "../../texts";
import { useGetClientConfig } from "../api/getClientConfig";

type ClientConfigContextType = ClientConfig & Config & { texts: Record<TextId, Text> };

const ClientConfigContext = createContext<ClientConfigContextType | null>(null);

export function ClientConfigProvider({ children }: PropsWithChildren) {
    const clientConfigQuery = useGetClientConfig();
    const configQuery = useGetConfig();
    const textsQuery = useGetTexts();

    const config = useMemo(
        () => ({ ...clientConfigQuery.data, ...configQuery.data, texts: textsQuery.data }),
        [clientConfigQuery.data, configQuery.data, textsQuery.data],
    );

    const contextValue = useMemo(() => config, [config]);

    if (clientConfigQuery.isPending || configQuery.isPending || textsQuery.isPending) {
        return <Spinner />;
    }

    return (
        <ClientConfigContext.Provider value={contextValue as ClientConfigContextType}>
            {children}
        </ClientConfigContext.Provider>
    );
}

export function useClientConfig() {
    const context = useContext(ClientConfigContext);

    if (context === null) {
        throw new Error("useClientConfig must be used within a ClientConfigProvider");
    }

    return context;
}
